import React, { useState } from "react";
import { useAppContext } from "../../AppProvider";
import { useHandlerContext } from "./providers/Handler";
import { getAllTimezones, getFieldError } from "../../utilities/utilities";
import ButtonUtility from "../../Components/Buttons/Utility";
import ButtonConfirm from "../../Components/Buttons/Confirm";
import { User } from "../../Models/User";
import AsyncSelect from "../../Components/Selects/AsyncSelect/AsyncSelect";
import { FetchParam } from "../../Models/FetchParam";
import { Company } from "../../Models/Company";
import { getAllCompanies } from "../../Components/Selects/AsyncSelect/api";
import SyncSelect from "../../Components/Selects/SyncSelect/SyncSelect";
import { useIndexUsersContext } from "./providers/Index";
import { CompanyOption } from "../../Components/Selects/AsyncSelect/styles";
import InputBoxIcon from "../../Components/Inputs/InputIcon/InputIcon";
import {
  faAt,
  faEye,
  faEyeSlash,
  faLoader,
} from "../../utilities/fontawesomeIcons";
import Switch from "../../Components/Switches/Switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Handler = () => {
  const { dictionary } = useAppContext();
  const { data, error, loader, callback, edit, onSubmit } = useHandlerContext();
  const indexUsersContext = useIndexUsersContext();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const modalConfig = indexUsersContext?.modalConfig;

  const handleChange = (
    name: string,
    value: string | boolean | number | object | null,
    key: string
  ) => {
    edit(
      {
        ...data,
        [name]: value,
      } as User,
      key as keyof User,
      false
    );
  };

  const timezones = getAllTimezones();
  return (
    <React.Fragment>
      {loader ? (
        <div className="text-center mt-2">
          <FontAwesomeIcon icon={faLoader} spin size="2x" />
        </div>
      ) : (
        <React.Fragment>
          <h3 className="text-center">{modalConfig?.title}</h3>
          <div className="d-grid grid-column mb-2">
            <div>
              <label className="label-2">{dictionary.registries.company}</label>
              <AsyncSelect
                name="owner"
                optionValue="id"
                optionLabel="name"
                value={data.owner ?? null}
                onSearch={(name: string, value: Company | Company[] | null) => {
                  handleChange(name, value, "owner");
                }}
                loadOptions={(inputValue, pagination) => {
                  const params: FetchParam = {
                    inputValue,
                    pagination,
                    sorting: { selector: "name", orderBy: "asc" },
                  };
                  return getAllCompanies(params);
                }}
                optionStyle={(option: Company) => (
                  <CompanyOption option={option} />
                )}
                isClearable={false}
                error={getFieldError(error, "owner")}
                placeholder={dictionary.actions.select}
              />
            </div>
          </div>
          <div
            className="d-grid grid-column mb-2"
            style={{
              gridTemplateColumns: `1fr 1fr`,
            }}
          >
            <div>
              <label className="label-2">{dictionary.registries.name}</label>
              <InputBoxIcon
                name="name"
                value={data?.name || ""}
                placeholder={"Mario"}
                type="text"
                onChange={({ target }) => {
                  handleChange(target.name, target.value, "name");
                }}
                error={getFieldError(error, "name")}
              />
            </div>
            <div>
              <label className="label-2">{dictionary.users.surname}</label>
              <InputBoxIcon
                name="surname"
                value={data?.surname || ""}
                placeholder={"Rossi"}
                type="text"
                onChange={({ target }) => {
                  handleChange(target.name, target.value, "surname");
                }}
                error={getFieldError(error, "surname")}
              />
            </div>
          </div>
          <div
            className="d-grid grid-column mb-2"
            style={{
              gridTemplateColumns: `1fr 1fr`,
            }}
          >
            <div>
              <label className="label-2">{dictionary.registries.email}</label>
              <InputBoxIcon
                name="email"
                value={data?.email || ""}
                placeholder={"mario.rossi@email.com"}
                type="text"
                onChange={({ target }) => {
                  handleChange(target.name, target.value, "email");
                }}
                icon={faAt}
                disabled={!!data.id}
                error={getFieldError(error, "email")}
              />
            </div>
            <div>
              <label className="label-2">{dictionary.locations.timezone}</label>
              <SyncSelect
                name="timeZone"
                value={data?.timeZone ?? null}
                onChange={(name: string, value: any | any[] | null) =>
                  handleChange(name, value, "timeZone")
                }
                options={timezones}
                isClearable={false}
                error={getFieldError(error, "timeZone")}
              />
            </div>
          </div>
          {!data.id && (
            <div
              className="d-grid grid-column mb-2"
              style={{
                gridTemplateColumns: `1fr 1fr`,
              }}
            >
              <div>
                <label className="label-2">{dictionary.users.password}</label>
                <InputBoxIcon
                  name="password"
                  value={data?.password || ""}
                  type={showPassword ? "text" : "password"}
                  onChange={({ target }) => {
                    handleChange(target.name, target.value, "password");
                  }}
                  onClickIcon={() => {
                    setShowPassword(!showPassword);
                  }}
                  classNameIcon="cursor-pointer"
                  icon={showPassword ? faEyeSlash : faEye}
                  error={getFieldError(error, "password")}
                />
              </div>
              <div>
                <label className="label-2">
                  {dictionary.users.confirm_password}
                </label>
                <InputBoxIcon
                  name="confirmPassword"
                  value={data?.confirmPassword || ""}
                  type={showConfirmPassword ? "text" : "password"}
                  onChange={({ target }) => {
                    handleChange(target.name, target.value, "confirmPassword");
                  }}
                  onClickIcon={() => {
                    setShowConfirmPassword(!showConfirmPassword);
                  }}
                  icon={showConfirmPassword ? faEyeSlash : faEye}
                  classNameIcon="cursor-pointer"
                  error={getFieldError(error, "confirmPassword")}
                />
              </div>
            </div>
          )}
          <div
            className="d-grid grid-column mb-2"
            style={{
              gridTemplateColumns: `1fr`,
            }}
          >
            <div className="text-end">
              <Switch
                name="enabled"
                checked={data.enabled ?? false}
                onChange={(e) => {
                  handleChange(e.target.name, !data.enabled, "enabled");
                }}
                label={dictionary.words.active}
              />
            </div>
          </div>
          <div className="d-flex mt-3">
            <ButtonUtility
              className="w-50 me-2"
              onClick={() => {
                if (callback) {
                  callback();
                }
              }}
              disabled={loader}
            >
              {dictionary.actions.cancel}
            </ButtonUtility>
            <ButtonConfirm
              className="w-50"
              onClick={() => onSubmit(data?.id)}
              disabled={loader}
              loading={loader}
            >
              {dictionary.actions.save}
            </ButtonConfirm>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default Handler;
